<template>
  <Listbox
    as="div"
    v-model="selected.users"
    @update:model-value="onInput"
    multiple
    class="z-20"
  >
    <ListboxButton
      :class="{
        'bg-sky-100 text-sky-800': selected.users.length > 0,
        'bg-gray-100 text-gray-900': selected.users.length < 1,
      }"
      class="flex whitespace-nowrap items-center rounded-sm border-transparent hover:bg-sky-100 hover:text-sky-800 transition py-2 px-3 sm:text-sm"
    >
      <span class="flex items-center">
        {{
          selected.users.length === 1
            ? selected.users[0]?.fullname
            : t("admin.search.filters.assignee")
        }}
        <span
          v-if="selected.users.length > 1"
          class="ml-1 inline-flex items-center rounded-full bg-sky-200 px-2 py-0.5 text-xs font-medium text-sky-800"
        >
          {{ selected.users.length }}
        </span>
      </span>
      <span class="ml-2">
        <ChevronDownIcon
          v-if="selected.users.length === 0"
          class="h-5 w-5 text-gray-900"
          aria-hidden="true"
        />
        <XMarkIcon
          v-else
          @click.prevent="clear"
          class="h-5 w-5 text-sky-800"
          aria-hidden="true"
        />
      </span>
    </ListboxButton>
    <transition
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <ListboxOptions
        class="absolute left-0 sm:left-auto w-full sm:w-auto z-10 mt-3 max-h-56 flex flex-col rounded-sm bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-sm"
      >
        <FilterSearchInput
          v-model="searchQuery"
          :placeholder="t('components.inputs.searchUsers')"
        />

        <div class="overflow-auto" v-if="filteredUsers.length > 0">
          <ListboxOption
            as="template"
            v-for="user in filteredUsers"
            :key="user.id"
            :value="user"
            v-slot="{ active, selected }"
          >
            <li
              :class="[
                active ? 'text-gray-900 bg-gray-100' : 'text-gray-900',
                'relative cursor-pointer select-none py-2 pl-3 pr-9',
              ]"
            >
              <div class="flex items-center space-x-2">
                <input
                  class="h-4 w-4 rounded-xs border-gray-300 text-brand-900 focus:outline-0 focus:ring-offset-0 focus:ring-0"
                  type="checkbox"
                  :checked="selected"
                />
                <user-avatar :user="user" />
                <span
                  :class="['truncate font-normal', selected && 'font-semibold']"
                >
                  {{ user.fullname }}
                </span>
              </div>
            </li>
          </ListboxOption>
        </div>

        <ListboxOption disabled v-else class="px-3 py-2 text-gray-500">
          {{ t("components.no_result") }}
        </ListboxOption>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script setup>
import {
  computed,
  defineEmits,
  defineProps,
  onBeforeMount,
  reactive,
  ref,
  watch,
} from "vue";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/vue";
import { XMarkIcon, ChevronDownIcon } from "@heroicons/vue/20/solid";
import { useI18n } from "vue-i18n";

import { useUsers } from "@/Tailwind/Mixins/useUsers";
import UserAvatar from "@/Tailwind/Components/UserAvatar";
import FilterSearchInput from "@/Tailwind/Components/Input/Filters/FilterSearchInput";

const { t } = useI18n();

const emit = defineEmits(["change"]);

const props = defineProps({
  value: Array,
  tenantIds: Array,
});

const users = computed(() => useUsers(props.tenantIds));
const selected = reactive({
  users: [],
});

// Search
const searchQuery = ref("");

const filteredUsers = computed(() => {
  if (!searchQuery.value) {
    return users.value;
  }

  return users.value.filter((user) =>
    user.fullname.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

onBeforeMount(() => {
  if (!props.value) {
    return;
  }

  selected.users =
    users.value.filter((user) => props.value.includes(user.id)) || [];
});

watch(
  () => props.value,
  (value) => {
    if (!value || value.length === 0) {
      selected.users = [];

      return;
    }

    selected.users =
      users.value.filter((user) => props.value.includes(user.id)) || [];
  }
);

const onInput = (value) => {
  emit(
    "change",
    value.map((user) => user.id)
  );
};

const clear = () => {
  selected.users = [];
  emit("change", []);
};
</script>
